import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  LayoutGrid,
  List,
  Calendar,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Archive,
} from 'lucide-react';
import clsx from 'clsx';

import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppInput } from '@/shared/components/ui/AppInput';
import { AppSelect } from '@/shared/components/ui/AppSelect';
import { AppCard } from '@/shared/components/ui/AppCard';
import { CampaignStatusBadge } from '@/shared/components/promote/CampaignStatusBadge';
import { PlatformTag } from '@/shared/components/promote/PlatformTag';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { CampaignStatus, AdPlatform } from '@/shared/types/enums';
import type { Campaign } from '@/shared/types/entities';
import { ROUTES } from '@/app/routes';

import { useCampaigns, useEvents } from '../../api/campaigns-queries';
import styles from './CampaignListPage.module.scss';

const ALL_STATUSES_VALUE = 'all-statuses';
const ALL_PLATFORMS_VALUE = 'all-platforms';

const STATUS_OPTIONS = [
  { value: ALL_STATUSES_VALUE, label: 'All Statuses' },
  { value: CampaignStatus.DRAFT, label: 'Draft' },
  { value: CampaignStatus.ACTIVE, label: 'Active' },
  { value: CampaignStatus.PAUSED, label: 'Paused' },
  { value: CampaignStatus.ARCHIVED, label: 'Archived' },
  { value: CampaignStatus.PENDING_REVIEW, label: 'Pending Review' },
  { value: CampaignStatus.COMPLETED, label: 'Completed' },
];

const PLATFORM_OPTIONS = [
  { value: ALL_PLATFORMS_VALUE, label: 'All Platforms' },
  { value: AdPlatform.META, label: 'Meta' },
  { value: AdPlatform.GOOGLE, label: 'Google' },
  { value: AdPlatform.TWITTER, label: 'X (Twitter)' },
];

const PAGE_SIZE = 12;

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(cents / 100);
}

function resolveEventName(campaign: Campaign, eventNames: Map<string, string>): string | undefined {
  return campaign.eventName ?? (campaign.eventId ? eventNames.get(campaign.eventId) : undefined);
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

const listItemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.25 },
  }),
};

export function CampaignListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES_VALUE);
  const [platformFilter, setPlatformFilter] = useState(ALL_PLATFORMS_VALUE);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showArchived, setShowArchived] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);

  const filters = useMemo(() => {
    const f: Record<string, unknown> = { page, pageSize: PAGE_SIZE };
    if (statusFilter !== ALL_STATUSES_VALUE) f.status = statusFilter;
    if (platformFilter !== ALL_PLATFORMS_VALUE) f.platform = platformFilter;
    return f;
  }, [statusFilter, platformFilter, page]);

  const { data, isLoading, isError } = useCampaigns(filters);

  const totalPages = data?.totalPages ?? 1;

  // Backend exposes neither a search param nor a status exclusion, so search and the
  // archived filter both narrow the loaded page client-side (page counts stay server-side).
  // An explicit Archived status filter shows them regardless of the toggle.
  const statusFilterIsArchived = statusFilter === CampaignStatus.ARCHIVED;
  const archivedVisible = showArchived || statusFilterIsArchived;

  const campaigns = useMemo(() => {
    const list = data?.data ?? [];
    const q = debouncedSearch.trim().toLowerCase();
    const searched = q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
    if (archivedVisible) return searched;
    return searched.filter((c) => c.status !== CampaignStatus.ARCHIVED);
  }, [data, debouncedSearch, archivedVisible]);

  // Campaign payloads carry eventId only, so resolve display names from the org's
  // events (cached and shared with the wizard).
  const { data: events } = useEvents();
  const eventNameById = useMemo(
    () => new Map((events ?? []).map((event) => [event.id, event.name])),
    [events],
  );

  // Distinguish "no campaigns at all" from "search matched none on this page".
  const hasActiveSearch = debouncedSearch.trim().length > 0;
  const isSearchEmpty = campaigns.length === 0 && hasActiveSearch && (data?.data.length ?? 0) > 0;
  // The server returned campaigns but the toggle hid every one of them.
  const isArchivedHiddenEmpty =
    campaigns.length === 0 &&
    !hasActiveSearch &&
    !archivedVisible &&
    (data?.data ?? []).some((c) => c.status === CampaignStatus.ARCHIVED);

  // Clamp out-of-range pages once results arrive (e.g. a filter shrank totalPages).
  useEffect(() => {
    if (data && page > totalPages) setPage(Math.max(1, totalPages));
  }, [data, page, totalPages]);

  const handleCreateCampaign = useCallback(() => {
    navigate({ to: ROUTES.campaignsNew });
  }, [navigate]);

  const handleCampaignClick = useCallback(
    (campaign: Campaign) => {
      navigate({ to: '/promote/campaigns/$id', params: { id: campaign.id } });
    },
    [navigate],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handlePlatformChange = useCallback((value: string) => {
    setPlatformFilter(value);
    setPage(1);
  }, []);

  const handleToggleArchived = useCallback(() => {
    setShowArchived((prev) => !prev);
    setPage(1);
  }, []);

  return (
    <PageContainer
      title="Campaigns"
      subtitle="Manage your advertising campaigns"
      actions={
        <AppButton size="lg" onClick={handleCreateCampaign}>
          <Plus size={18} />
          Create Campaign
        </AppButton>
      }
    >
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <div className={styles.searchWrapper}>
              <Search size={16} className={styles.searchIcon} />
              <AppInput
                placeholder="Search campaigns..."
                value={search}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
            </div>
            <AppSelect
              placeholder="All Statuses"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onValueChange={handleStatusChange}
            />
            <AppSelect
              placeholder="All Platforms"
              options={PLATFORM_OPTIONS}
              value={platformFilter}
              onValueChange={handlePlatformChange}
            />
          </div>
          <div className={styles.toolbarActions}>
            <AppButton
              variant={archivedVisible ? 'secondary' : 'ghost'}
              onClick={handleToggleArchived}
              aria-pressed={archivedVisible}
              disabled={statusFilterIsArchived}
              title={
                statusFilterIsArchived
                  ? 'The Archived status filter is already showing archived campaigns.'
                  : undefined
              }
            >
              <Archive size={16} />
              {archivedVisible ? 'Hide Archived' : 'Show Archived'}
            </AppButton>
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={clsx(styles.viewButton, viewMode === 'grid' && styles.viewButtonActive)}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                type="button"
                className={clsx(styles.viewButton, viewMode === 'table' && styles.viewButtonActive)}
                onClick={() => setViewMode('table')}
                aria-label="Table view"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {isLoading && (
          <div className={styles.loadingGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        )}

        {isError && (
          <div className={styles.errorState}>
            <p>Failed to load campaigns. Please try again.</p>
          </div>
        )}

        {!isLoading && !isError && campaigns.length === 0 && (
          <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.emptyIcon}>
              <LayoutGrid size={48} />
            </div>
            {isSearchEmpty ? (
              <>
                <h3 className={styles.emptyTitle}>No matching campaigns</h3>
                <p className={styles.emptyDescription}>
                  No campaigns on this page match your search. Try a different term.
                </p>
              </>
            ) : isArchivedHiddenEmpty ? (
              <>
                <h3 className={styles.emptyTitle}>Only archived campaigns</h3>
                <p className={styles.emptyDescription}>
                  Every campaign here is archived. Show them to take a look.
                </p>
                <AppButton size="lg" variant="secondary" onClick={handleToggleArchived}>
                  <Archive size={18} />
                  Show Archived
                </AppButton>
              </>
            ) : (
              <>
                <h3 className={styles.emptyTitle}>No campaigns yet</h3>
                <p className={styles.emptyDescription}>
                  Create your first advertising campaign to start promoting your events.
                </p>
                <AppButton size="lg" onClick={handleCreateCampaign}>
                  <Plus size={18} />
                  Create Your First Campaign
                </AppButton>
              </>
            )}
          </motion.div>
        )}

        {!isLoading && !isError && campaigns.length > 0 && viewMode === 'grid' && (
          <div className={styles.grid}>
            <AnimatePresence mode="popLayout">
              {campaigns.map((campaign, index) => {
                const eventName = resolveEventName(campaign, eventNameById);

                return (
                  <motion.div
                    key={campaign.id}
                    custom={index}
                    variants={listItemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    layout
                  >
                    <AppCard
                      hoverable
                      className={styles.campaignCard}
                      onClick={() => handleCampaignClick(campaign)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCampaignClick(campaign);
                        }
                      }}
                    >
                      <div className={styles.cardHeader}>
                        <h3 className={styles.cardName}>{campaign.name}</h3>
                        <CampaignStatusBadge status={campaign.status} />
                      </div>
                      <div className={styles.cardMeta}>
                        <PlatformTag platform={campaign.platform} />
                        {eventName && <span className={styles.eventName}>Event: {eventName}</span>}
                      </div>
                      <div className={styles.cardFooter}>
                        <div className={styles.cardStat}>
                          <DollarSign size={14} />
                          <span>{formatCurrency(campaign.budgetCents, campaign.currency)}</span>
                          <span className={styles.budgetType}>{campaign.budgetType}</span>
                        </div>
                        <div className={styles.cardStat}>
                          <Calendar size={14} />
                          <span>{formatDate(campaign.startDate)}</span>
                          {campaign.endDate && <span>- {formatDate(campaign.endDate)}</span>}
                        </div>
                      </div>
                    </AppCard>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {!isLoading && !isError && campaigns.length > 0 && viewMode === 'table' && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Platform</th>
                  <th>Budget</th>
                  <th>Date Range</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {campaigns.map((campaign, index) => (
                    <motion.tr
                      key={campaign.id}
                      custom={index}
                      variants={listItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className={styles.tableRow}
                      onClick={() => handleCampaignClick(campaign)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCampaignClick(campaign);
                        }
                      }}
                    >
                      <td>
                        <div className={styles.tableCampaignName}>
                          <span>{campaign.name}</span>
                          {resolveEventName(campaign, eventNameById) && (
                            <span className={styles.tableEventName}>
                              Event: {resolveEventName(campaign, eventNameById)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <CampaignStatusBadge status={campaign.status} />
                      </td>
                      <td>
                        <PlatformTag platform={campaign.platform} />
                      </td>
                      <td>
                        {formatCurrency(campaign.budgetCents, campaign.currency)}{' '}
                        <span className={styles.budgetType}>{campaign.budgetType}</span>
                      </td>
                      <td>
                        {formatDate(campaign.startDate)}
                        {campaign.endDate && ` - ${formatDate(campaign.endDate)}`}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && totalPages > 1 && (
          <div className={styles.pagination}>
            <AppButton
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={16} />
              Previous
            </AppButton>
            <span className={styles.pageInfo}>
              Page {page} of {totalPages}
            </span>
            <AppButton
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <ChevronRight size={16} />
            </AppButton>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
